import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Calendar, Building2, AlertCircle, Bell, Clock, Eye, RefreshCw, UserX, ChevronRight, Target, TrendingUp, TrendingDown, Wallet, DollarSign } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { format, differenceInDays, startOfMonth, endOfMonth, subDays } from "date-fns";
import { ResilientFetchBanner, runWithSettled } from "@/lib/resilientFetch";
import SessionsWarningBanner from "@/components/SessionsWarningBanner";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";

const AdminDashboard = ({ user, onLogout }) => {
  const { default_timezone } = useBrand();
  const [stats, setStats] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [expired, setExpired] = useState([]);
  const [renewalTab, setRenewalTab] = useState("expiring");
  const [cohortDeadlines, setCohortDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [failures, setFailures] = useState([]);
  // Feb 2026 — Financial summary card (4-source aggregation)
  const [finPeriod, setFinPeriod] = useState("month"); // month | fy | 30d
  const [finSummary, setFinSummary] = useState(null);
  const [finLoading, setFinLoading] = useState(false);
  const navigate = useNavigate();

  const FETCHERS = {
    "dashboard stats":    () => api.get("/dashboard/stats"),
    "renewals":           () => api.get("/enrollments/expiring?days=7&include_expired=true"),
    "cohort deadlines":   () => api.get("/cohorts/dashboard-summary"),
  };

  const applyResults = useCallback((results) => {
    if (results["dashboard stats"]) {
      setStats(results["dashboard stats"].data.stats);
    }
    if (results["renewals"]) {
      setExpiringSoon(results["renewals"].data.expiring_soon || []);
      setExpired(results["renewals"].data.expired || []);
    }
    if (results["cohort deadlines"]) {
      setCohortDeadlines(results["cohort deadlines"].data.cohort_deadlines || []);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const { results, failures: f } = await runWithSettled(FETCHERS);
    applyResults(results);
    setFailures(f);
    setLoading(false);
    // Cohort summary failure is non-essential — never block UI for it.
    const blocking = f.filter((x) => x.label !== "cohort deadlines");
    if (blocking.length === f.length || (f.length && !blocking.length)) {
      // either all blocking or only cohort failed — banner will reflect
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyResults]);

  const retryFailed = async () => {
    if (!failures.length) return;
    setRetrying(true);
    const onlyFailed = Object.fromEntries(failures.map((f) => [f.label, FETCHERS[f.label]]));
    const { results, failures: stillFailing } = await runWithSettled(onlyFailed);
    applyResults(results);
    setFailures(stillFailing);
    setRetrying(false);
    if (!stillFailing.length) toast.success("Dashboard refreshed");
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feb 2026 — resolve current period date range for the financial summary card
  const finRange = React.useMemo(() => {
    const now = new Date();
    if (finPeriod === "fy") {
      // Indian FY: Apr 1 – Mar 31
      const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      return {
        date_from: `${y}-04-01`,
        date_to: `${y + 1}-03-31`,
        label: `FY ${String(y).slice(-2)}-${String(y + 1).slice(-2)}`,
      };
    }
    if (finPeriod === "30d") {
      return {
        date_from: format(subDays(now, 29), "yyyy-MM-dd"),
        date_to: format(now, "yyyy-MM-dd"),
        label: "Last 30 days",
      };
    }
    return {
      date_from: format(startOfMonth(now), "yyyy-MM-dd"),
      date_to: format(endOfMonth(now), "yyyy-MM-dd"),
      label: format(now, "MMMM yyyy"),
    };
  }, [finPeriod]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFinLoading(true);
      try {
        const res = await api.get(
          `/dashboard/financial-summary?date_from=${finRange.date_from}&date_to=${finRange.date_to}`
        );
        if (!cancelled) setFinSummary(res.data);
      } catch (e) {
        if (!cancelled) setFinSummary(null);
      } finally {
        if (!cancelled) setFinLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [finRange.date_from, finRange.date_to]);

  const handleDiscontinue = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to discontinue ${studentName}? This will expire all their active enrollments.`)) return;
    try {
      await api.put(`/users/${studentId}/discontinue`);
      toast.success(`${studentName} has been discontinued`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to discontinue student");
    }
  };

  const statsLoadFailed = failures.some((f) => f.label === "dashboard stats");
  const statCards = [
    {
      title: "Total Students",
      value: stats?.total_students ?? 0,
      icon: Users,
      color: "from-violet-500 to-purple-500",
      link: "/students",
    },
    {
      title: "Active Batches",
      value: stats?.total_batches ?? 0,
      icon: Calendar,
      color: "from-pink-500 to-rose-500",
      link: "/classes",
    },
    {
      title: "Studios",
      value: stats?.total_studios ?? 0,
      icon: Building2,
      color: "from-blue-500 to-cyan-500",
      link: "/studios",
    },
    {
      title: "Expiring Soon",
      value: stats?.expiring_soon ?? 0,
      icon: AlertCircle,
      color: "from-orange-500 to-amber-500",
      link: null,
      action: () => {
        const el = document.getElementById("renewal-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
        setRenewalTab("expiring");
      },
    },
  ];

  const totalRenewals = expiringSoon.length + expired.length;
  const currentList = renewalTab === "expiring" ? expiringSoon : expired;

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="dashboard-title">
            Welcome back, {user.name}
          </h1>
          <p className="text-sm text-slate-600 mt-1">Here's what's happening in your studios today</p>
        </div>

        <SessionsWarningBanner className="mb-4" />

        {failures.length > 0 && (
          <ResilientFetchBanner
            failedLabels={failures.map((f) => f.label)}
            onRetry={retryFailed}
            retrying={retrying}
            className="mb-4"
            testid="dashboard-fetch-error"
          />
        )}

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8" data-testid="dashboard-stats-skeleton">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100 animate-pulse">
                <div className="w-12 h-12 rounded-xl bg-slate-200 mb-4" />
                <div className="h-8 w-12 bg-slate-200 rounded mb-2" />
                <div className="h-3 w-24 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              const CardWrapper = stat.link ? Link : "div";
              const wrapperProps = stat.link
                ? { to: stat.link }
                : { onClick: stat.action, role: "button", tabIndex: 0 };
              return (
                <CardWrapper key={stat.title} {...wrapperProps} className="block">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all cursor-pointer group"
                    data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                    </div>
                    <p className="text-3xl font-bold font-heading text-slate-900">
                      {statsLoadFailed ? <span className="text-slate-300">—</span> : stat.value}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">{stat.title}</p>
                  </motion.div>
                </CardWrapper>
              );
            })}
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
          <h2 className="text-lg font-bold font-heading text-slate-900 mb-4" data-testid="quick-actions-title">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Link to="/students">
              <Button data-testid="enroll-student-button" className="w-full h-10 sm:h-12 rounded-full bg-primary hover:bg-primary/90 font-medium text-xs sm:text-sm">
                Enroll Student
              </Button>
            </Link>
            <Link to="/classes">
              <Button data-testid="create-batch-button" className="w-full h-10 sm:h-12 rounded-full bg-secondary hover:bg-secondary/90 font-medium text-xs sm:text-sm">
                Create Batch
              </Button>
            </Link>
            <Link to="/attendance">
              <Button data-testid="mark-attendance-button" className="w-full h-10 sm:h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:opacity-90 font-medium text-xs sm:text-sm">
                Attendance
              </Button>
            </Link>
            <Link to="/announcements">
              <Button data-testid="send-announcement-button" className="w-full h-10 sm:h-12 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:opacity-90 font-medium text-xs sm:text-sm">
                Announce
              </Button>
            </Link>
          </div>
        </div>

        {/* Feb 2026 — Financial Summary (4-source aggregation) */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100 mb-8" data-testid="financial-summary-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-bold font-heading text-slate-900 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                Financial Summary
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {finRange.label} · {finRange.date_from} → {finRange.date_to}
              </p>
            </div>
            <div className="flex gap-1 bg-slate-100 rounded-full p-1">
              {[
                { key: "month", label: "This Month" },
                { key: "fy", label: "Financial Year" },
                { key: "30d", label: "Last 30 days" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`fin-period-${opt.key}`}
                  onClick={() => setFinPeriod(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    finPeriod === opt.key
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {finLoading && !finSummary ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-pulse">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 rounded-xl bg-slate-100" />
              ))}
            </div>
          ) : finSummary ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3" data-testid="fin-enrollment">
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Enrollments</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">₹{(finSummary.enrollment_revenue || 0).toLocaleString("en-IN")}</p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3" data-testid="fin-rental">
                  <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">Rentals</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">₹{(finSummary.rental_revenue || 0).toLocaleString("en-IN")}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3" data-testid="fin-outside-revenue">
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Other Revenue</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">₹{(finSummary.outside_revenue || 0).toLocaleString("en-IN")}</p>
                </div>
                <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3" data-testid="fin-outflow">
                  <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wider">Total Outflow</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">₹{(finSummary.total_outflow || 0).toLocaleString("en-IN")}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Expenses ₹{(finSummary.outside_expense || 0).toLocaleString("en-IN")} · Payroll ₹{(finSummary.payroll_paid || 0).toLocaleString("en-IN")}</p>
                </div>
              </div>
              <div className={`rounded-xl p-4 flex items-center justify-between gap-3 ${
                (finSummary.net_income || 0) >= 0
                  ? "bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100"
                  : "bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-100"
              }`} data-testid="fin-net-income">
                <div className="flex items-center gap-3">
                  {(finSummary.net_income || 0) >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-rose-600" />
                  )}
                  <div>
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Net Income</p>
                    <p className="text-[11px] text-slate-500">Gross ₹{(finSummary.gross_income || 0).toLocaleString("en-IN")} − Outflow ₹{(finSummary.total_outflow || 0).toLocaleString("en-IN")}</p>
                  </div>
                </div>
                <p className={`text-2xl font-bold ${
                  (finSummary.net_income || 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                }`}>
                  ₹{(finSummary.net_income || 0).toLocaleString("en-IN")}
                </p>
              </div>
              <div className="flex justify-end mt-3">
                <Link to="/ledger" className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1">
                  Manage Ledger <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 py-6 text-center">Financial data unavailable.</p>
          )}
        </div>

        {/* Cohort Deadlines Section */}
        {cohortDeadlines.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8" data-testid="cohort-deadlines">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-violet-100">
                <Target className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold font-heading text-slate-900">Cohort Deadlines</h2>
                <p className="text-xs text-slate-500">{cohortDeadlines.length} active cohort{cohortDeadlines.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="space-y-3">
              {cohortDeadlines.map((cd, i) => {
                const isUrgent = cd.should_prompt_interest || (cd.days_remaining !== null && cd.days_remaining <= 7);
                return (
                  <button
                    key={cd.cohort_id}
                    type="button"
                    data-testid={`cohort-deadline-${i}`}
                    onClick={() => navigate(`/classes?openCohortBatchId=${cd.batch_id}&focus=interest`)}
                    className={`w-full text-left p-4 rounded-xl border transition hover:shadow-sm ${isUrgent ? "border-amber-200 bg-amber-50 hover:bg-amber-100/70" : "border-slate-100 bg-slate-50 hover:bg-slate-100"}`}
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="font-semibold text-sm text-slate-900 truncate">{cd.batch_name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cd.threshold_met ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {cd.status_label}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                      <div>
                        <p className="text-slate-500">Start</p>
                        <p className="font-bold">{cd.start_date ? fmt(cd.start_date, "dd MMM", default_timezone) : "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Progress</p>
                        <p className="font-bold">{cd.completed_sessions}/{cd.target_sessions}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Est. End</p>
                        <p className="font-bold">{cd.estimated_end_date ? fmt(cd.estimated_end_date, "dd MMM", default_timezone) : "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Interested</p>
                        <p className="font-bold text-green-600">{cd.interested} <span className="text-slate-400">/ {cd.min_students} min</span></p>
                      </div>
                      <div>
                        <p className="text-slate-500">Enrolled</p>
                        <p className="font-bold">{cd.enrolled_count}</p>
                      </div>
                    </div>
                    {isUrgent && cd.status === "active" && (
                      <p className="mt-2 text-xs text-amber-700 font-medium">
                        {cd.days_remaining} day{cd.days_remaining !== 1 ? "s" : ""} until estimated end — tap to start interest capture
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Renewal Reminders Section */}
        {totalRenewals > 0 && (
          <div id="renewal-section" className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            {/* Section Header */}
            <div className="p-6 pb-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Bell className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="text-lg font-bold font-heading text-slate-900" data-testid="renewal-reminders-title">
                  Renewal Reminders
                </h2>
                <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                  {totalRenewals}
                </span>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-200">
                <button
                  data-testid="tab-expiring-soon"
                  onClick={() => setRenewalTab("expiring")}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    renewalTab === "expiring"
                      ? "border-orange-500 text-orange-700"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Expiring Soon ({expiringSoon.length})
                </button>
                <button
                  data-testid="tab-expired"
                  onClick={() => setRenewalTab("expired")}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    renewalTab === "expired"
                      ? "border-red-500 text-red-700"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Expired ({expired.length})
                </button>
              </div>
            </div>

            {/* Renewal List */}
            <div className="p-6 pt-4">
              {currentList.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">
                  {renewalTab === "expiring" ? "No subscriptions expiring soon" : "No expired subscriptions"}
                </p>
              ) : (
                <div className="space-y-3 max-h-[28rem] overflow-y-auto">
                  {currentList.map((enrollment, index) => {
                    const endDate = new Date(enrollment.end_date);
                    const now = new Date();
                    const daysDiff = differenceInDays(endDate, now);
                    const isExpired = daysDiff < 0;
                    const daysText = isExpired
                      ? `Expired ${Math.abs(daysDiff)} day${Math.abs(daysDiff) !== 1 ? "s" : ""} ago`
                      : `${daysDiff} day${daysDiff !== 1 ? "s" : ""} left`;

                    return (
                      <motion.div
                        key={enrollment.enrollment_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        data-testid={`renewal-card-${index}`}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          {/* Student Info */}
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                              {enrollment.student_name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{enrollment.student_name}</p>
                              <p className="text-xs text-slate-500">
                                {enrollment.batch_name}{enrollment.style_name ? ` - ${enrollment.style_name}` : ""}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-500">
                                  {format(endDate, "dd MMM yyyy")}
                                </span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  isExpired
                                    ? "bg-red-100 text-red-700"
                                    : daysDiff <= 3
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}>
                                  {daysText}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            <Button
                              data-testid={`renewal-view-${index}`}
                              size="sm"
                              variant="outline"
                              className="rounded-full text-xs h-8 px-3"
                              onClick={() => navigate(`/students?student_id=${enrollment.student_id}`)}
                            >
                              <Eye className="w-3.5 h-3.5 sm:mr-1" />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                            <Button
                              data-testid={`renewal-renew-${index}`}
                              size="sm"
                              className="rounded-full text-xs h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => navigate(`/students?student_id=${enrollment.student_id}&tab=enrollments`)}
                            >
                              <RefreshCw className="w-3.5 h-3.5 sm:mr-1" />
                              <span className="hidden sm:inline">Renew</span>
                            </Button>
                            <Button
                              data-testid={`renewal-discontinue-${index}`}
                              size="sm"
                              variant="outline"
                              className="rounded-full text-xs h-8 px-3 border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handleDiscontinue(enrollment.student_id, enrollment.student_name)}
                            >
                              <UserX className="w-3.5 h-3.5 sm:mr-1" />
                              <span className="hidden sm:inline">Discontinue</span>
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
};

export default AdminDashboard;
